import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, formatCurrency, formatNumber, costLineTypeKey } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';

type Line = { id: string; line_type: string; description: string | null; quantity: number; unit_cost: number | null; hourly_rate: number | null; currency: string };
type Rate = { id: string; name: string; hourly_rate: number };
type Part = { id: string; name: string; unit_cost: number | null };
type Supplier = { id: string; name: string };

const amountOf = (l: Line) => (l.line_type === 'labour' ? (l.hourly_rate ?? 0) : (l.unit_cost ?? 0)) * (l.quantity ?? 0);

/** Cost lines for a work order: labour at a frozen rate, parts at frozen unit
 *  cost (decrements stock), supplier/other amounts. Ops-only (RLS). */
export function WorkOrderCosts({ workOrderId, propertyId }: { workOrderId: string; propertyId: string }) {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [lines, setLines] = useState<Line[]>([]);
  const [rates, setRates] = useState<Rate[]>([]);
  const [parts, setParts] = useState<Part[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [kind, setKind] = useState<'labour' | 'part' | 'subcontract' | 'other'>('labour');
  const [sel, setSel] = useState({ rate: '', part: '', supplier: '', qty: '1', amount: '', description: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await supabase.from('work_order_cost_lines').select('id,line_type,description,quantity,unit_cost,hourly_rate,currency').eq('work_order_id', workOrderId).is('deleted_at', null).order('created_at');
    setLines((r.data as Line[]) ?? []);
  }, [workOrderId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.from('labour_rates').select('id,name,hourly_rate').is('deleted_at', null).order('name').then((r) => setRates(r.data ?? []));
    supabase.from('parts').select('id,name,unit_cost').is('deleted_at', null).order('name').then((r) => setParts((r.data as Part[]) ?? []));
    supabase.from('suppliers').select('id,name').is('deleted_at', null).order('name').then((r) => setSuppliers(r.data ?? []));
  }, []);

  async function add() {
    const qty = Number(sel.qty) || 1;
    let payload: any = { organization_id: orgId, property_id: propertyId, work_order_id: workOrderId, line_type: kind, quantity: qty, currency, description: sel.description.trim() || null };
    if (kind === 'labour') {
      const rate = rates.find((x) => x.id === sel.rate); if (!rate) return;
      payload.hourly_rate = rate.hourly_rate; payload.description = payload.description ?? rate.name;
    } else if (kind === 'part') {
      const part = parts.find((x) => x.id === sel.part); if (!part) return;
      payload.part_id = part.id; payload.unit_cost = part.unit_cost ?? 0; payload.description = payload.description ?? part.name;
    } else if (kind === 'subcontract') {
      if (sel.supplier) payload.supplier_id = sel.supplier;
      payload.unit_cost = Number(sel.amount) || 0;
    } else {
      payload.unit_cost = Number(sel.amount) || 0;
    }
    setBusy(true);
    const res = await supabase.from('work_order_cost_lines').insert(payload);
    setBusy(false);
    if (!res.error) { setSel({ rate: '', part: '', supplier: '', qty: '1', amount: '', description: '' }); load(); }
  }

  const total = lines.reduce((s, l) => s + amountOf(l), 0);
  const inp = 'h-9 rounded-md border border-border bg-card px-2 text-sm outline-none';

  return (
    <div className="flex flex-col gap-2 border-t border-divider p-6">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-[15px] font-semibold">{t('cost.title')}</h3>
        <span className="font-mono text-sm">{formatCurrency(total, currency, i18n.language)}</span>
      </div>
      {lines.length === 0 && <div className="rounded-[10px] bg-[#F7F8FA] p-3 text-sm text-muted">{t('cost.none')}</div>}
      {lines.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-2 rounded-[10px] border border-divider px-3 py-2 text-sm">
          <span className="flex-1 truncate">{l.description ?? t(costLineTypeKey(l.line_type))}<span className="ml-2 text-xs text-muted">{t(costLineTypeKey(l.line_type))}</span></span>
          <span className="font-mono text-xs text-muted">{formatNumber(l.quantity, i18n.language, 3)} × {formatCurrency((l.line_type === 'labour' ? l.hourly_rate : l.unit_cost) ?? 0, l.currency, i18n.language)}</span>
          <span className="font-mono">{formatCurrency(amountOf(l), l.currency, i18n.language)}</span>
        </div>
      ))}
      {/* add line */}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <select className={inp} value={kind} onChange={(e) => setKind(e.target.value as any)}>
          <option value="labour">{t('cost.labour')}</option>
          <option value="part">{t('cost.part')}</option>
          <option value="subcontract">{t('cost.subcontract')}</option>
          <option value="other">{t('cost.other')}</option>
        </select>
        {kind === 'labour' && <>
          <select className={cn(inp, 'min-w-[120px]')} value={sel.rate} onChange={(e) => setSel({ ...sel, rate: e.target.value })}><option value="">{t('cost.selectRate')}</option>{rates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.hourly_rate})</option>)}</select>
          <input className={cn(inp, 'w-20')} type="number" step="0.25" value={sel.qty} onChange={(e) => setSel({ ...sel, qty: e.target.value })} placeholder={t('cost.hours')} />
        </>}
        {kind === 'part' && <>
          <select className={cn(inp, 'min-w-[120px]')} value={sel.part} onChange={(e) => setSel({ ...sel, part: e.target.value })}><option value="">{t('cost.selectPart')}</option>{parts.map((p) => <option key={p.id} value={p.id}>{p.name}{p.unit_cost != null ? ` (${p.unit_cost})` : ''}</option>)}</select>
          <input className={cn(inp, 'w-16')} type="number" value={sel.qty} onChange={(e) => setSel({ ...sel, qty: e.target.value })} placeholder={t('cost.quantity')} />
        </>}
        {(kind === 'subcontract') && <select className={cn(inp, 'min-w-[120px]')} value={sel.supplier} onChange={(e) => setSel({ ...sel, supplier: e.target.value })}><option value="">{t('part.supplier')}</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
        {(kind === 'subcontract' || kind === 'other') && <input className={cn(inp, 'w-24')} type="number" step="0.01" value={sel.amount} onChange={(e) => setSel({ ...sel, amount: e.target.value })} placeholder={t('cost.amount')} />}
        <Button size="sm" disabled={busy} onClick={add}>+ {t('cost.add')}</Button>
      </div>
    </div>
  );
}
